import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useRef } from 'react';

import { Icon, Overlay, Popover } from '~/components/blocks';
import { NotificationPopover } from '~/components/layouts/SharedAppShell/NotificationPopover';
import { useFetchUnreadTemporaryPermissions } from '~/components/partials/MedicineNoteNotificationBadge/use-fetch-unread_temporary_permissions';
import { announcementModalState } from '~/state/layouts/announcement_modal/atoms';
import {
  medicationFollowupNotificationPanelState,
  notificationPopoverState,
  notificationState,
} from '~/state/layouts/SharedAppShell/atoms';
import { entryNotificationDrawerState } from '~/state/partials/entry_notification_drawer/atoms';
import { medicineNoteNotificationPanelState } from '~/state/partials/medicine_note_notification_panel/atoms';
import { organizationNotificationDrawerState } from '~/state/partials/organization_notification_drawer/atoms';

const Root = styled('div')(({ theme }) =>
  css({
    position: 'relative',
    cursor: 'pointer',
    padding: theme.space.m,
  }),
);

const Badge = styled('div')(({ theme }) =>
  css({
    display: 'inline-block',
    position: 'absolute',
    left: `calc(${theme.space.xl} - ${theme.space.xs})`,
    top: `calc(${theme.space.m} - ${theme.space.xs})`,
    backgroundColor: theme.colors.colorPallete.pink,
    borderRadius: '50%',
    color: theme.colors.text.white,
    fontSize: theme.fontSizes.xs,
    width: `calc(${theme.space.l} + ${theme.space.xs})`,
    height: `calc(${theme.space.l} + ${theme.space.xs})`,
    lineHeight: `calc(${theme.space.l} + ${theme.space.xs})`,
    textAlign: 'center',
    fontWeight: 'bold',
  }),
);

const PopoverFrame = styled(Popover)(() =>
  css({
    left: '-16px !important', // react-popperで自動調整されてしまうため、右側を開けるように調整
  }),
);

export const NotificationBadge = () => {
  const { unreadCount } = useFetchUnreadTemporaryPermissions();
  const anckerRef = useRef<HTMLDivElement>(null);

  const [{ isOpen }, setNotificationPopoverState] = useAtom(notificationPopoverState);
  const resetOrganizationNotificationDrawerState = useResetAtom(
    organizationNotificationDrawerState,
  );
  const resetMedicationFollowupNotificationPanelState = useResetAtom(
    medicationFollowupNotificationPanelState,
  );
  const resetEntryNotificationDrawerState = useResetAtom(entryNotificationDrawerState);
  const resetMedicineNoteNotificationPanelState = useResetAtom(medicineNoteNotificationPanelState);

  const [{ receptionCount, entryCount, followupCount, medicineNoteCount }, setNotificationState] =
    useAtom(notificationState);
  const count = receptionCount + entryCount + followupCount + medicineNoteCount;

  const resetAnnouncementModalState = useResetAtom(announcementModalState);
  const handleClick = useCallback(() => {
    setNotificationPopoverState((_state) => ({ ..._state, isOpen: !isOpen }));
    resetAnnouncementModalState();
  }, [isOpen, resetAnnouncementModalState, setNotificationPopoverState]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      // 要素外をクリックした場合に閉じる
      if (e.target === e.currentTarget) {
        setNotificationPopoverState((_state) => ({ ..._state, isOpen: false }));

        resetOrganizationNotificationDrawerState();
        resetMedicationFollowupNotificationPanelState();
        resetEntryNotificationDrawerState();
        resetMedicineNoteNotificationPanelState();
      }
    },
    [
      setNotificationPopoverState,
      resetOrganizationNotificationDrawerState,
      resetMedicationFollowupNotificationPanelState,
      resetEntryNotificationDrawerState,
      resetMedicineNoteNotificationPanelState,
    ],
  );

  useEffect(() => {
    setNotificationState((_state) => ({ ..._state, medicineNoteCount: unreadCount }));
  }, [setNotificationState, unreadCount]);

  return (
    <div>
      <Root onClick={handleClick} ref={anckerRef}>
        {count > 0 ? <Badge>{count}</Badge> : null}
        <Icon size="xl" color="navy" icon="notification" />
      </Root>
      {anckerRef.current && (
        <Overlay transparent open={isOpen} onClick={handleClose}>
          <PopoverFrame inside open={isOpen} placement={'bottom'} anckerEl={anckerRef.current}>
            <NotificationPopover />
          </PopoverFrame>
        </Overlay>
      )}
    </div>
  );
};
